import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { TurnInAssignmentCounts, TurnInDisposition, TurnInRow } from './types';
import { useTableSort } from '@/hooks/useTableSort';
import { SortableTableCell } from '@/components/shared/SortableTableCell';

const formatFriendlyDate = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed) return '--';
  const parsed = new Date(trimmed);
  if (!Number.isNaN(parsed.getTime())) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(parsed);
  }
  return trimmed;
};

export interface TurnInPlannerTableProps {
  isLoading: boolean;
  customerId?: string;
  rows: TurnInRow[];
  rowAssignmentCounts: Map<string, TurnInAssignmentCounts>;
  onAddAllocation: (rowId: string, disposition: TurnInDisposition) => void;
}

export const TurnInPlannerTable = ({
  isLoading,
  customerId,
  rows,
  rowAssignmentCounts,
  onAddAllocation,
}: TurnInPlannerTableProps) => {
  const { sortField, sortDirection, onSort, sortedRows } = useTableSort(
    rows as unknown as Record<string, unknown>[],
  );
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', maxHeight: '70vh' }}>
      <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
        <Table size="small" stickyHeader aria-label="Turn-in planner items">
          <TableHead>
            <TableRow>
              <SortableTableCell field="lin" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>LIN</SortableTableCell>
              <SortableTableCell field="nsn" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>NSN</SortableTableCell>
              <SortableTableCell field="item" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Nomenclature</SortableTableCell>
              <SortableTableCell field="receiptType" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Type</SortableTableCell>
              <SortableTableCell field="condition" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Condition</SortableTableCell>
              <SortableTableCell field="expirationDate" sortField={sortField} sortDirection={sortDirection} onSort={onSort}>Expiration Date</SortableTableCell>
              <TableCell align="center" sx={{ fontWeight: 600 }}>In Cart</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {customerId
                      ? 'No hand receipt items found for this customer'
                      : 'No customer selected'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              (sortedRows as unknown as TurnInRow[]).map((row) => {
                const counts = rowAssignmentCounts.get(row.id) ?? {
                  recoverable: 0,
                  non_recoverable: 0,
                  cleared: 0,
                };
                const assignedTotal = counts.recoverable + counts.non_recoverable + counts.cleared;
                const isFullyAssigned = assignedTotal >= row.totalQty;

                return (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.lin}</TableCell>
                    <TableCell>{row.nsn}</TableCell>
                    <TableCell>{row.item}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.receiptType}
                        color={row.receiptType === 'PHR' ? 'primary' : 'secondary'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.condition}
                        color={row.conditionTone}
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const expDate = row.expirationDate ?? null;
                        if (!expDate) {
                          return (
                            <Typography variant="body2" color="text.secondary">
                              --
                            </Typography>
                          );
                        }
                        const parsed = new Date(expDate);
                        const daysUntil = Number.isNaN(parsed.getTime())
                          ? null
                          : Math.floor((parsed.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                        const expired = daysUntil !== null && daysUntil < 0;
                        const approaching = daysUntil !== null && daysUntil >= 0 && daysUntil <= 60;
                        const color = expired ? 'error' : approaching ? 'warning' : 'success';
                        return (
                          <Chip size="small" label={formatFriendlyDate(expDate)} color={color} />
                        );
                      })()}
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: isFullyAssigned ? 'success.main' : 'inherit',
                        }}
                      >
                        {assignedTotal}/{row.totalQty}
                        {isFullyAssigned ? ' ✓' : ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ButtonGroup variant="outlined" size="small" disabled={isFullyAssigned}>
                        <Button
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddAllocation(row.id, 'recoverable');
                          }}
                        >
                          Svc ({counts.recoverable})
                        </Button>
                        <Button
                          color="warning"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAddAllocation(row.id, 'non_recoverable');
                          }}
                        >
                          Non ({counts.non_recoverable})
                        </Button>
                        {row.recoverableState === 'nonRecoverable' ? (
                          <Button
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddAllocation(row.id, 'cleared');
                            }}
                          >
                            Clr ({counts.cleared})
                          </Button>
                        ) : null}
                      </ButtonGroup>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Box>
    </Paper>
  );
};
